// ─── Workshop Truck Availability Tracker ───
// useDashboard – Fetches dashboard KPIs + availability history
// 60-second cache to avoid hammering the API
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useCallback, useRef } from 'react'
import type { DashboardData, AvailabilitySnapshot } from '@/types'
import { api } from '@/utils/api'

interface ApiDashboardResponse {
  success: boolean
  data: DashboardData
}

interface ApiHistoryResponse {
  success: boolean
  data: AvailabilitySnapshot[]
}

interface UseDashboardReturn {
  data: DashboardData | null
  historyData: AvailabilitySnapshot[]
  isLoading: boolean
  error: string | null
  lastSynced: string | null
  refresh: () => Promise<void>
}

const CACHE_TTL_MS = 60_000 // 60 seconds

export function useDashboard(): UseDashboardReturn {
  const [data, setData] = useState<DashboardData | null>(null)
  const [historyData, setHistoryData] = useState<AvailabilitySnapshot[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastSynced, setLastSynced] = useState<string | null>(null)

  const lastFetchRef = useRef<number>(0)

  const refresh = useCallback(async (force = false) => {
    // Simple 60-second cache – skip re-fetch if still fresh
    const now = Date.now()
    if (!force && lastFetchRef.current > 0 && now - lastFetchRef.current < CACHE_TTL_MS) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const [dashboardRes, historyRes] = await Promise.all([
        api.get<ApiDashboardResponse>('/api/dashboard'),
        api.get<ApiHistoryResponse>('/api/availability-history'),
      ])

      setData(dashboardRes.data)
      setHistoryData(historyRes.data ?? [])
      setLastSynced(new Date().toISOString())
      lastFetchRef.current = Date.now()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Force refresh (bypasses cache) – exposed to NavBar button
  const forceRefresh = useCallback(async () => {
    await refresh(true)
  }, [refresh])

  // Auto-fetch on first mount
  if (!data && !isLoading && !error) {
    refresh()
  }

  return {
    data,
    historyData,
    isLoading,
    error,
    lastSynced,
    refresh: forceRefresh,
  }
}
